import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import api from '../services/api';

/**
 * Animation lettre par lettre sans couper les mots : chaque mot est un bloc nowrap,
 * les retours à la ligne n'interviennent qu'entre les mots.
 */
function letterAnimatedTitle(text, letterVariants, startOffset) {
  let i = startOffset;
  const parts = text.split(/(\s+)/);
  const nodes = parts.map((segment, si) => {
    if (/^\s+$/.test(segment)) {
      return <span key={`sp-${si}`}>{segment}</span>;
    }
    return (
      <span key={`wd-${si}`} className="inline-block whitespace-nowrap align-baseline">
        {segment.split('').map((char, ci) => (
          <motion.span
            key={ci}
            custom={i++}
            initial="hidden"
            animate="visible"
            variants={letterVariants}
            className="inline-block"
          >
            {char}
          </motion.span>
        ))}
      </span>
    );
  });
  return { nodes, nextOffset: i };
}

const Hero = () => {
  const { language } = useLanguage();
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.getSiteData().then(response => {
      setContent(response.data[language].hero);
    }).catch(err => console.error('Error loading hero data:', err));
  }, [language]);

  if (!content) return <div className="min-h-screen bg-[#0a0a0b]" />;

  const letterVariants = {
    hidden: { opacity: 0, y: 50, filter: 'blur(10px)' },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: {
        delay: i * 0.03,
        duration: 0.5,
        ease: [0.6, 0.01, 0.05, 0.95]
      }
    })
  };

  const titleText = content.title;
  const highlightText = content.titleHighlight;
  const line1 = letterAnimatedTitle(titleText, letterVariants, 0);
  const line2 = letterAnimatedTitle(highlightText, letterVariants, line1.nextOffset);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-b from-[#0a0a0b] via-[#0f0f10] to-[#0a0a0b]">
      {/* Animated background grid */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(0, 217, 255, 0.1) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(0, 217, 255, 0.1) 1px, transparent 1px)`,
          backgroundSize: '100px 100px'
        }} />
      </div>

      {/* Gradient orbs */}
      <motion.div
        className="absolute top-1/4 -left-32 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"
        animate={{
          x: [0, 100, 0],
          y: [0, 50, 0],
          scale: [1, 1.2, 1]
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        className="absolute bottom-1/4 -right-32 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl"
        animate={{
          x: [0, -100, 0],
          y: [0, -50, 0],
          scale: [1, 1.3, 1]
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Floating badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-8 backdrop-blur-sm"
        >
          <Sparkles className="w-4 h-4 text-cyan-400" />
          <span className="text-sm text-cyan-300 font-medium">
            {content.availabilityBadge || 'Available for freelance'}
          </span>
        </motion.div>

        {/* Main title with letter animation (mots entiers, pas de coupure au milieu) */}
        <div className="mb-6 max-w-[min(100%,56rem)] mx-auto">
          <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-3 sm:mb-4 hyphens-none text-balance leading-[1.1] sm:leading-tight break-normal">
            {line1.nodes}
          </h1>
          <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold bg-gradient-to-r from-cyan-400 via-blue-500 to-violet-500 bg-clip-text text-transparent hyphens-none text-balance leading-[1.1] sm:leading-tight break-normal">
            {line2.nodes}
          </h1>
        </div>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="text-lg sm:text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto mb-12 text-pretty hyphens-none break-words px-1"
        >
          {content.subtitle}
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.8, duration: 0.8 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <motion.button
            whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(0, 217, 255, 0.5)' }}
            whileTap={{ scale: 0.95 }}
            className="group relative px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-semibold overflow-hidden transition-all duration-300"
          >
            <span className="relative z-10 flex items-center gap-2">
              {content.cta1}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </span>
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-blue-600 to-violet-600"
              initial={{ x: '100%' }}
              whileHover={{ x: 0 }}
              transition={{ duration: 0.3 }}
            />
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-8 py-4 border-2 border-cyan-500/30 text-cyan-400 rounded-lg font-semibold hover:bg-cyan-500/10 transition-all duration-300"
          >
            {content.cta2}
          </motion.button>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.5, duration: 1 }}
          className="absolute bottom-12 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-10 border-2 border-cyan-500/30 rounded-full p-1"
          >
            <motion.div
              animate={{ y: [0, 20, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-1 h-2 bg-cyan-400 rounded-full mx-auto"
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
